// src/App.jsx
import { Suspense, useEffect, useRef } from "react";
import { BrowserRouter, useLocation } from "react-router-dom";
import AppRoutes from "@/Routes.jsx";
import { AuthProvider, useAuth } from "@/contexts/AuthContext.jsx";
import { LanguageProvider } from "@/contexts/LanguageContext.jsx";
import { ThemeProvider } from "@/contexts/ThemeContext.jsx";
import { __setAuth } from "@/stores/auth";
import supabase from "@/lib/supabase";
import { getSupabaseRuntimeConfig } from "@/services/supabaseClient.js";
import { resolveValidatedSession } from "@/lib/authSession";
import RouteFallback from "@/sot/primitives/RouteFallback.jsx";
import ProductionLaunchGate from "@/components/auth/ProductionLaunchGate.jsx";
import LegalAcceptanceGate from "@/components/legal/LegalAcceptanceGate.jsx";
import "@/sot/afrimed-design-system.css";
import CookieConsent from "@/components/consent/CookieConsent.jsx";
import { writeUserActivityEvent } from "@/services/userTruthTracking.js";
import {
  getDocumentNavigationTelemetry,
  getPrivacySafeTelemetryRoute,
} from "@/lib/onboardingNavigationTelemetry.js";

function PassiveJourneyTelemetry() {
  const location = useLocation();
  const { user, profile } = useAuth() || {};
  const lastHiddenAtRef = useRef(0);
  const lastRouteKeyRef = useRef("");
  const route = getPrivacySafeTelemetryRoute(location);
  const role =
    String(profile?.role || user?.user_metadata?.primary_role || user?.user_metadata?.role || "").trim() ||
    "";

  useEffect(() => {
    const userId = String(user?.id || "").trim();
    if (!userId) return;
    const key = `${userId}:${route}`;
    if (lastRouteKeyRef.current === key) return;
    lastRouteKeyRef.current = key;
    void writeUserActivityEvent({
      userId,
      role,
      route,
      eventType: "route_changed",
      metadata: {
        pathname: location.pathname || "/",
        search_present: Boolean(location.search),
        hash_present: Boolean(location.hash),
        ...getDocumentNavigationTelemetry(),
      },
    }).catch(() => {});
  }, [location.hash, location.pathname, location.search, role, route, user?.id]);

  useEffect(() => {
    const userId = String(user?.id || "").trim();
    if (!userId || typeof document === "undefined") return undefined;

    const emit = (eventType, metadata = {}) => {
      void writeUserActivityEvent({
        userId,
        role,
        route,
        eventType,
        metadata: {
          ...getDocumentNavigationTelemetry(),
          ...metadata,
        },
      }).catch(() => {});
    };

    const handleVisibility = () => {
      if (document.visibilityState === "hidden") {
        lastHiddenAtRef.current = Date.now();
        emit("app_page_hidden", { reason: "visibilitychange" });
        return;
      }
      if (document.visibilityState === "visible" && lastHiddenAtRef.current) {
        emit("session_resumed", {
          hidden_ms: Math.max(0, Date.now() - lastHiddenAtRef.current),
        });
      }
    };

    const handlePageHide = () => {
      lastHiddenAtRef.current = Date.now();
      emit("app_page_hidden", { reason: "pagehide" });
    };

    document.addEventListener("visibilitychange", handleVisibility);
    window.addEventListener("pagehide", handlePageHide);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibility);
      window.removeEventListener("pagehide", handlePageHide);
    };
  }, [role, route, user?.id]);

  return null;
}

function RoutedApp() {
  return (
    <ProductionLaunchGate>
      <LegalAcceptanceGate>
        <PassiveJourneyTelemetry />
        <AppRoutes />
      </LegalAcceptanceGate>
    </ProductionLaunchGate>
  );
}

function LocalProofReadiness() {
  return (
    <main
      className="min-h-[100dvh] bg-[rgb(var(--clr-surface-container-low))] text-on-surface"
      data-testid="local-proof-readiness"
    >
      <div className="afrimed-rail afrimed-rail--comfortable grid min-h-[100dvh] place-items-center">
        <section className="flex w-full max-w-[560px] flex-col items-center text-center" data-testid="local-proof-readiness-empty-state">
          <img
            src="/icons/medical-institution/not_available.svg"
            alt=""
            aria-hidden="true"
            className="h-[76px] w-[76px]"
            draggable="false"
          />
          <h1 className="mt-4 text-title-large text-on-surface">Local proof services are not ready</h1>
          <p className="mt-2 max-w-[560px] text-body-large text-on-surface-var">
            Start the isolated local Supabase stack with supabase start, then run npm run local:proof. Port 4031 never uses staging data.
          </p>
        </section>
      </div>
    </main>
  );
}

export default function App() {
  const runtimeConfig = getSupabaseRuntimeConfig();

  if (runtimeConfig.source === "blocked-local-remote-config") {
    return <LocalProofReadiness />;
  }

  useEffect(() => {
    try {
      if (document?.body) document.body.dataset.appReady = "1";
      window.dispatchEvent(new Event("afrimed:app-ready"));
    } catch {
      // noop
    }
  }, []);

  useEffect(() => {
    let sub;
    const isAuthCallbackPath =
      typeof window !== "undefined" &&
      String(window.location.pathname || "").startsWith("/auth/callback");

    if (isAuthCallbackPath) {
      return () => sub?.unsubscribe?.();
    }

    (async () => {
      // 1. Validate the stored session immediately on boot.
      const session = await resolveValidatedSession({ signOutOnFailure: false });

      // 2. Sync the basic auth state (user only, let Context handle the profile)
      __setAuth({ user: session?.user ?? null, profile: null });

      // 3. Listen for future auth changes (login/logout)
      const { data } = supabase.auth.onAuthStateChange(async (_event, sess) => {
        const nextSession = sess?.access_token
          ? await resolveValidatedSession({ signOutOnFailure: false })
          : null;
        __setAuth({ user: nextSession?.user ?? null, profile: null });
      });

      sub = data.subscription;
    })();

    return () => sub?.unsubscribe?.();
  }, []);

  return (
    <AuthProvider>
      <ThemeProvider>
        <LanguageProvider>
          <BrowserRouter>
            <Suspense fallback={<RouteFallback label="Loading AfriMed…" />}>
              <RoutedApp />
            </Suspense>
          </BrowserRouter>
        </LanguageProvider>
      </ThemeProvider>

      {/* global + passive */}
      <CookieConsent />
    </AuthProvider>
  );
}
